import { Fragment } from "react"
import type { FC, ReactElement } from "react"

import isEmpty from "lodash-es/isEmpty"
import map from "lodash-es/map"

import {
    ESpecialChar,
    ETypographyWeight,
    EWaveLoaderVariant,
    SpecialChars,
    Typography,
    WaveLoader
} from "@/3514/components"
import { type TScreenBreakpoints, useScreenBreakpoints } from "@/3514/hooks"
import { getCssIdentifierFromFileName } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"

import { type ICoachGroupSessionSummaryItemModel as ISummaryModel } from "./coach.group.api.types"
import styles from "./coach.group.styles"
import uiConfig from "./coach.group.ui.config"
import uiUtils from "./coach.group.ui.utils"

const {
    entities: { summary: classes }
} = styles
const { getSummaryRichTextClasses, summaryRichTextCssIdentifier } = uiConfig

type TProps = { summaries: ISummaryModel[]; isLoading: boolean }
type TBlockProps = { summary: ISummaryModel; isLoading: boolean }

const { renderSummaryDetail } = uiUtils

const displayName: string = "CoachGroupSummaries"
const animationCssIdentifier: string = getCssIdentifierFromFileName(displayName, "al")

const Block: FC<TBlockProps> = ({ summary, isLoading }: TBlockProps): ReactElement => (
    <li className={classes.blocks.list.item({ isStub: isLoading })}>
        <Typography
            text={
                <Fragment>
                    {summary.title}
                    {SpecialChars[ESpecialChar.EmptySpace]}
                    {SpecialChars[ESpecialChar.Dash]}
                    {SpecialChars[ESpecialChar.EmptySpace]}
                </Fragment>
            }
            className={classes.blocks.list.text()}
            weight={ETypographyWeight.Bold}
        />

        {renderSummaryDetail({
            id: summaryRichTextCssIdentifier,
            richClasses: getSummaryRichTextClasses(summaryRichTextCssIdentifier),
            content: summary.detail
        })}
    </li>
)

const CoachGroupSummaries: FC<TProps> = ({ summaries, isLoading = false }: TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    return isEmpty(summaries) ? null : (
        <div className={classes.blocks.container()}>
            <ul className={classes.blocks.list.container()}>
                {map<ISummaryModel, ReactElement>(
                    summaries,
                    (s: ISummaryModel, idx: number): ReactElement => (
                        <Block key={`${displayName}-${idx}`} summary={s} isLoading={isLoading} />
                    )
                )}
            </ul>

            {isLoading && (
                <WaveLoader
                    cssIdentifier={animationCssIdentifier}
                    wrapperClassName={classes.loader()}
                    title={t("coachSide.groupPage.summary.loader.title")}
                    titleHint={t("coachSide.groupPage.summary.loader.hint")}
                    variant={EWaveLoaderVariant.DarkBlue}
                    width={breakpoints.xs || breakpoints.sm ? "max-content" : "fit-content"}
                />
            )}
        </div>
    )
}

CoachGroupSummaries.displayName = displayName

export default CoachGroupSummaries
